import { SectionHead } from '@/public/features/problem/ui/problemNew/parts'
import { useCallback, useState } from 'react'
import { InfoIcon } from '@/shared/ui/icons/InfoIcon'
import { Selectbutton } from '@/shared/ui/Selectbutton'
import { TARGET_LIFECYCLE, TARGET_SITUATION, useCommonCodes, useDongs } from '@/shared/lib/codes'
import { figmaLabel } from '@/shared/lib/figmaLabel'
import type { ProblemNewDraft } from '@/public/features/problem/types'
import { TARGET_MAX } from '@/public/features/problem/constants/problemNew'
import { KakaoPointMap } from '@/shared/ui/KakaoPointMap'
import {
  addressMetaText,
  useKakaoAddressSearch,
  type AddressCandidate,
} from '@/public/features/problem/composables/useKakaoAddressSearch'

export function StepTarget({ draft }: { draft: ProblemNewDraft }) {
  const lifecycle = useCommonCodes(TARGET_LIFECYCLE)
  const situation = useCommonCodes(TARGET_SITUATION)
  const targets = [...(lifecycle.data ?? []), ...(situation.data ?? [])]
  const isFull = draft.targetCodes.length >= TARGET_MAX

  return (
    <>
      <section className="flex flex-col gap-3">
        <SectionHead
          title="누가 대상인가요?"
          desc={`문제를 겪는 대상의 특성을 최대 ${TARGET_MAX}개까지 선택해 주세요.`}
        />
        <div className="flex flex-wrap content-start gap-x-2 gap-y-2.5 [&>.ui-selectbutton]:flex-[1_1_calc((100%-16px)/3)] [&>.ui-selectbutton]:whitespace-nowrap">
          {targets.map((t) => (
            <Selectbutton
              key={t.code}
              label={figmaLabel(t.code, t.label)}
              isOn={draft.targetCodes.includes(t.code)}
              isBlocked={isFull}
              onSelect={() => draft.toggleTarget(t.code)}
            />
          ))}
        </div>
      </section>

      <AddressSection draft={draft} />
    </>
  )
}
function AddressSection({ draft }: { draft: ProblemNewDraft }) {
  const dongs = useDongs()
  const search = useKakaoAddressSearch()
  const { address } = draft
  const [query, setQuery] = useState(address.text)

  const pick = useCallback(
    async (c: AddressCandidate) => {
      // 동 이름만 검색한 결과는 행정동코드가 비어 온다 — 좌표로 되물어 채운다.
      const resolved = await search.resolve(c)
      const known = (dongs.data ?? []).find((d) => d.code === resolved.regionCode)
      draft.setAddress({
        text: resolved.text,
        regionCode: known?.code ?? '',
        // 이름도 우리 목록의 것을 쓴다 — 표기가 흔들리면 안내 문구가 서로 달라진다.
        matchedName: known?.name ?? resolved.regionName,
        legalCode: resolved.legalCode,
        legalName: resolved.legalName,
        // 우편번호는 없을 수 있다 — 없으면 빈 문자열이고 본문에서 키가 빠진다.
        postalCode: resolved.postalCode ?? '',
        // 좌표는 지도에만 쓴다 — 본문에 싣지 않는다.
        lat: resolved.lat,
        lng: resolved.lng,
      })
      setQuery(resolved.text)
      search.clear()
    },
    [dongs.data, draft, search],
  )

  return (
    <section className="flex flex-col gap-3">
      <SectionHead
        title="어느 동네인가요?"
        desc="문제가 발생하는 곳의 주소를 찾아주세요."
      />
      <div className="flex items-start gap-2">
        <input
          className="min-h-10 rounded-lg border bg-white-50 px-3 py-2.5 text-body-sm text-black-600 placeholder:text-[#808080] min-w-0 flex-1 shrink-0 grow basis-0 rounded-[10px] border-white-400"
          value={query}
          placeholder="예: 하안동, 광명로"
          onChange={(e) => {
            setQuery(e.target.value)
            if (address.regionCode !== '') {
              draft.setAddress({
                text: '',
                regionCode: '',
                matchedName: '',
                legalCode: '',
                legalName: '',
                postalCode: '',
                lat: null,
                lng: null,
              })
            }
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault()
              void search.search(query)
            }
          }}
          aria-label="문제가 발생하는 곳의 주소"
        />
        <button
          type="button"
          className="ui-pressable inline-flex cursor-pointer items-center justify-center border border-white-700 font-base whitespace-nowrap h-10 shrink-0 rounded-[10px] bg-white-200 px-3 py-2.5 text-body-sm text-black-600 disabled:cursor-not-allowed disabled:border-white-300 disabled:text-white-500"
          onClick={() => void search.search(query)}
          disabled={search.isLoading}
        >
          주소 찾기
        </button>
      </div>

      {search.isLoading && (
        <p className="m-0 flex items-center gap-1 text-label font-medium text-violet-600 [&>svg]:h-4 [&>svg]:w-4 [&>svg]:shrink-0" role="status">
          <InfoIcon />
          <span>주소를 찾는 중입니다.</span>
        </p>
      )}

      {search.error !== null && (
        <p className="m-0 flex items-center gap-1 text-label font-medium text-yellow-900 [&>svg]:h-4 [&>svg]:w-4 [&>svg]:shrink-0" role="alert">
          <InfoIcon />
          <span>{search.error}</span>
        </p>
      )}

      {search.results.length > 0 && (
        <ul className="mt-2 mb-0 flex max-h-[232px] list-none flex-col overflow-y-auto rounded-[10px] border border-white-400 p-0 [&>li:first-child>button]:border-t-0" aria-label="주소 검색 결과">
          {search.results.map((c) => (
            <li key={c.id ?? `${c.jibun}-${c.text}`}>
              <button
                type="button"
                className="ui-pressable flex w-full cursor-pointer flex-col gap-0.5 border-0 border-t border-t-white-200 bg-none px-3 py-2.5 text-left"
                onClick={() => void pick(c)}
              >
                {c.placeName !== undefined && (
                  <span className="text-body-sm font-medium text-black-600">{c.placeName}</span>
                )}
                <span
                  className={
                    c.placeName === undefined
                      ? 'text-body-sm font-medium text-black-600'
                      : 'text-label text-black-100'
                  }
                >
                  {c.text}
                </span>
                {c.jibun !== c.text && <span className="text-label text-black-100">{c.jibun}</span>}
                {addressMetaText(c.regionName, c.legalName, c.postalCode) !== '' && (
                  <span className="text-label text-black-100">
                    {addressMetaText(c.regionName, c.legalName, c.postalCode)}
                  </span>
                )}
              </button>
            </li>
          ))}
        </ul>
      )}

      {search.hasSearched && !search.isLoading && search.error === null && search.results.length === 0 && (
        <p className="m-0 flex items-center gap-1 text-label font-medium text-yellow-900 [&>svg]:h-4 [&>svg]:w-4 [&>svg]:shrink-0" role="alert">
          <InfoIcon />
          <span>찾는 주소가 없습니다. 동 이름이나 도로명으로 다시 찾아주세요.</span>
        </p>
      )}

      {address.lat !== null && address.lng !== null && address.regionCode !== '' && (
        <KakaoPointMap lat={address.lat} lng={address.lng} />
      )}

      {address.regionCode !== '' && (
        <>
          <p className="m-0 flex items-center gap-1 text-label font-medium text-violet-600 [&>svg]:h-4 [&>svg]:w-4 [&>svg]:shrink-0">
            <InfoIcon />
            <span>{address.matchedName} 으로 접수됩니다.</span>
          </p>
          <p className="m-0 pl-5 text-label text-black-100">
            {addressMetaText(address.matchedName, address.legalName, address.postalCode)}
          </p>
        </>
      )}
      {address.text === '' && address.matchedName !== '' && (
        <p className="m-0 flex items-center gap-1 text-label font-medium text-yellow-900 [&>svg]:h-4 [&>svg]:w-4 [&>svg]:shrink-0" role="alert">
          <InfoIcon />
          <span>광명시 안의 주소만 접수할 수 있습니다.</span>
        </p>
      )}
    </section>
  )
}
